import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, MessageSquare, LineChart, Phone, Settings as SettingsIcon, Wrench, BookOpen, Mic, Save, ChevronRight, Pencil, PhoneCall, Copy, RefreshCw, X, Folder, CheckCircle2, Globe, Search } from "lucide-react";
import { TestAgentDialog } from "@/components/TestAgentDialog";
import { useAgent, useCreateAgent, useUpdateAgent, AgentConfig } from "@/services/assistant.query";
import { isNotFound } from "@/lib/api-client";
import { useNativeTools } from "@/services/tool.query";
import { END_CALL_DESCRIPTION } from "@/services/tool.service";
import { splitDynamicPrompt } from "@/utils/tool-name-utils";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { toast } from "sonner";
import { AddToolDialog } from "@/components/Tools/AddToolDialog";
import { KbFolderSelectorDialog } from "@/components/KnowledgeBase/KbFolderSelectorDialog";
import { KbTestSearch } from "@/components/KnowledgeBase/KbTestSearch";
import { UnifiedToolsList } from "@/components/Tools/UnifiedToolsList";
import { PostCallAnalysisEditor } from "@/components/Analytics/PostCallAnalysisEditor";
import { useKbStores } from "@/hooks/use-knowledge-base";
import { CalIntegrationCard } from "@/components/CalIntegration/CalIntegrationCard";
import { VoiceSelector } from "@/components/VoiceSelector/VoiceSelector";
import { DEFAULT_GEMINI_VOICE, isGeminiVoiceId } from "@/constants/geminiVoices";
import {
  AGENT_MODELS,
  DEFAULT_TTS_MODEL,
  GEMINI_3_8_LIVE,
  MODEL_HELP,
  THINKING_LEVELS,
  TTS_MODELS,
  TTS_MODEL_LABELS,
  agentModelOf,
  isTextModel,
  modelLabel,
  thinkingHelp,
  thinkingLabel,
  thinkingLevelOf,
  ttsModelOf,
  type AgentModel,
  type ThinkingLevel,
  type TtsModel,
} from "@/constants/liveModels";
import { ACCENTS, ACCENT_HELP, ACCENT_LABELS, DEFAULT_ACCENT, accentOf, type Accent } from "@/constants/accents";
import { VOCABULARY_HELP, vocabularyError, vocabularyTerms, vocabularyText } from "@/constants/vocabulary";
import { liveRuntime } from "@/utils/tool-options";

import { WebWidgetConfig } from "@/components/WebWidget/WebWidgetConfig";
import { Database } from "lucide-react";
import { SystemPromptEditor } from "@/components/AgentEditor/SystemPromptEditor";
import { KbMatchStrictness } from "@/components/AgentEditor/KbMatchStrictness";
import { KbResultsPerSearch } from "@/components/AgentEditor/KbResultsPerSearch";
import { KbAutoSearch } from "@/components/AgentEditor/KbAutoSearch";
import { CallerLanguages } from "@/components/AgentEditor/CallerLanguages";
import { callerLanguagesOf } from "@/constants/callerLanguages";
import {
  DEEPGRAM_VOCABULARY_NOTE,
  DEFAULT_TRANSCRIBER,
  TRANSCRIBERS,
  TRANSCRIBER_HELP,
  TRANSCRIBER_LABELS,
  type Transcriber,
  transcriberOf,
} from "@/constants/transcribers";
import { hasReadyKnowledgeBase } from "@/utils/kb-auto-search";
import { isFeatureOn } from "@/services/feature-flags.service";
import { analyzePrompt, type PromptAnalysis } from "@/lib/prompt-template";
import { KB_MIN_SIMILARITY_DEFAULT, kbMinSimilarityOf } from "@/utils/kb-match-strictness";
import { KB_TOP_K_DEFAULT, kbTopKOf } from "@/utils/kb-results-per-search";
import { fileCount } from "@/utils/kb-upload-rules";
import { MAX_CALL_DURATION_MINUTES } from "@/utils/call-limits";

export default function AgentEditor() {
  const { id: agentId } = useParams();
  const navigate = useNavigate();
  const { currentWorkspaceId } = useWorkspace();

  const isNewAgent = agentId === "new";
  const { data: agentData, error: agentError } = useAgent(agentId);
  // A deleted agent (or one the user cannot see): the function answers 404.
  const agentGone = isNotFound(agentError);
  // The built-ins, whole — the tools panel below pages the full list, and this
  // must see every native row whichever page it falls on. Not for a gone agent:
  // no read may send its id again, on a retry or when the window regains focus.
  const { data: nativeTools = [] } = useNativeTools(agentGone ? undefined : agentId);
  // The list is newest first, so this is the newest native row of that name.
  const nativeTool = (name: string) =>
    nativeTools.find((t) => t.is_native && t.tool_name === name) ?? null;
  const endCallTool = nativeTool("end_call");
  const sendSmsTool = nativeTool("send_sms");
  const transferCallTool = nativeTool("transfer_call");
  const updateAgent = useUpdateAgent();
  const createAgent = useCreateAgent(currentWorkspaceId || "");

  // Core state
  const [agentName, setAgentName] = useState("New Agent");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [promptAnalysis, setPromptAnalysis] = useState<PromptAnalysis | null>(null);
  // A prompt that ALREADY fails to parse must not lock the user out of their
  // own agent — they may have had `{{` in a JSON example long before this
  // feature existed. Block Publish only once they've actually edited it.
  const [promptBrokenOnLoad, setPromptBrokenOnLoad] = useState(false);
  const [promptEdited, setPromptEdited] = useState(false);
  const [voiceId, setVoiceId] = useState("Charon");
  // The engine (config.llm.model) and, on a text model, the voice it speaks with (config.voice.ttsModel).
  const [model, setModel] = useState<AgentModel>(GEMINI_3_8_LIVE);
  const [ttsModel, setTtsModel] = useState<TtsModel>(DEFAULT_TTS_MODEL);
  // How long a text model thinks before each reply (config.settings.thinkingLevel); none on 3.8 Live.
  const [thinkingLevel, setThinkingLevel] = useState<ThinkingLevel>();
  // The accent its English is spoken with, on either engine (config.language).
  const [accent, setAccent] = useState<Accent>(DEFAULT_ACCENT);
  // On a text model, the words its speech recognition listens for, one per line (config.vocabulary).
  const [vocabulary, setVocabulary] = useState("");
  // The languages its callers speak, most spoken first (config.settings.callerLanguages): on 3.8 Live, and on a text
  // model heard by Deepgram.
  const [callerLanguages, setCallerLanguages] = useState<string[]>([]);
  // Who hears the caller on a text model (config.transcriber.provider; owner, 2026-10-31).
  const [transcriber, setTranscriber] = useState<Transcriber>(DEFAULT_TRANSCRIBER);
  const [toolIds, setToolIds] = useState<string[]>([]);
  const [kbStoreIds, setKbStoreIds] = useState<string[]>([]);
  const [kbMinSimilarity, setKbMinSimilarity] = useState(KB_MIN_SIMILARITY_DEFAULT);
  const [kbTopK, setKbTopK] = useState(KB_TOP_K_DEFAULT);
  const [kbAutoSearch, setKbAutoSearch] = useState(false);
  const [endCallDescription, setEndCallDescription] = useState(END_CALL_DESCRIPTION);
  const [sipUri, setSipUri] = useState<string>("");
  const [isPublishing, setIsPublishing] = useState(false);

  // Initial message state - default to enabled
  const [initialMessageEnabled, setInitialMessageEnabled] = useState(true);

  // Post-call analysis state
  const [postCallAnalysisEnabled, setPostCallAnalysisEnabled] = useState(false);
  const [postCallAnalysisFields, setPostCallAnalysisFields] = useState<Array<{
    name: string;
    description: string;
    type: "string" | "enum" | "number" | "boolean";
    required?: boolean;
    choices?: string[];
  }>>([]);

  // Call settings state
  const [endCallOnSilence, setEndCallOnSilence] = useState(true);
  const [maxCallDuration, setMaxCallDuration] = useState(10);
  const [silenceTimeoutMinutes, setSilenceTimeoutMinutes] = useState(2);
  const [voicemailDetection, setVoicemailDetection] = useState(true);
  const [voicemailAction, setVoicemailAction] = useState<"hangup" | "leave_message">("hangup");
  const [voicemailMessage, setVoicemailMessage] = useState("");

  // Native tools state
  const [transferCallEnabled, setTransferCallEnabled] = useState(false);
  const [transferCallMode, setTransferCallMode] = useState<"static" | "dynamic">("static");
  const [transferCallDestination, setTransferCallDestination] = useState("");
  const [transferCallDescription, setTransferCallDescription] = useState("");
  const [transferCallPrompt, setTransferCallPrompt] = useState("");
  const [transferCallType, setTransferCallType] = useState<"cold" | "warm">("cold");
  const [endCallEnabled, setEndCallEnabled] = useState(isNewAgent);
  const [endCallConfigOpen, setEndCallConfigOpen] = useState(false);
  const [sendSmsEnabled, setSendSmsEnabled] = useState(false);
  const [sendSmsConfigOpen, setSendSmsConfigOpen] = useState(false);
  const [sendSmsDescription, setSendSmsDescription] = useState("");
  const [sendSmsContentType, setSendSmsContentType] = useState<"prompt" | "static">("prompt");
  const [sendSmsStaticMessage, setSendSmsStaticMessage] = useState("");

  // Advanced audio features
  // const [affectiveDialogEnabled, setAffectiveDialogEnabled] = useState(false);
  // const [proactiveAudioEnabled, setProactiveAudioEnabled] = useState(false);

  // Google Search state
  const [enableGoogleSearch, setEnableGoogleSearch] = useState(false);
  // The tool dialogs (a saved agent's only) explain their options by the model the
  // agent runs on now: the one it is saved with, read as the bot reads it, so a model
  // picked in the dropdown counts from its Publish. 3.1 is no longer offered, so it
  // lasts only until the next save.
  const toolRuntime = liveRuntime(agentData?.config?.llm?.model);
  // Only a text model speaks through a voice model and runs automatic knowledge-base search.
  const textModel = isTextModel(model);
  // The traditional engine's controls (Model, Thinking level, Voice model, Words to recognise) and the Automatic
  // search switch show only while shared/feature-flags.json offers them; hidden, Publish keeps what is stored.
  const engineOffered = isFeatureOn("traditionalEngine");
  const kbAutoSearchOffered = isFeatureOn("kbAutoSearch");
  // The Transcriber dropdown: a text model's, while both flags offer it. Caller Languages show on 3.8 Live, and on a
  // text model only while Deepgram hears it (Gemini Transcribe Live on a text model is sent no language codes).
  const transcriberShown = engineOffered && isFeatureOn("deepgramTranscriber") && textModel;
  const languagesShown = !textModel || (transcriberShown && transcriber === "deepgram");
  // Deepgram takes the words to recognise as keyterms, up to its limit.
  const deepgramVocabulary = transcriberShown && transcriber === "deepgram" && vocabularyTerms(vocabulary).length > 0;
  const vocabularyProblem = textModel ? vocabularyError(vocabularyTerms(vocabulary)) : null;
  // Put on a text model, from 3.8 Live or another text model, an agent starts at that model's default level, its
  // first (owner, 2026-10-21: a level kept from 3.8 Flash left Flash-Lite on Low). A stored level still loads as
  // saved. The Select calls this only when the model changes.
  const pickModel = (value: string) => {
    const next = agentModelOf(value);
    if (isTextModel(next)) setThinkingLevel(THINKING_LEVELS[next][0]);
    setModel(next);
  };

  // Webhook state
  const [webhookUrl, setWebhookUrl] = useState("");

  // Web Widget state
  const [webWidgetSettings, setWebWidgetSettings] = useState({
    enabled: false,
    theme: "auto" as "light" | "dark" | "auto",
    primaryColor: "#3B82F6",
    position: "bottom-right" as "bottom-right" | "bottom-left",
    buttonSize: "medium" as "small" | "medium" | "large",
    showBranding: true,
    avatarType: "default" as "default" | "custom",
    avatarUrl: "",
    integrationMode: "floating" as "floating" | "headless",
  });

  // Test dialog state
  const [testDialogOpen, setTestDialogOpen] = useState(false);

  // Fetch knowledge base stores
  const { data: kbStores = [] } = useKbStores(currentWorkspaceId || undefined);

  useEffect(() => {
    // Show notification for newly created agents (detected via location state or timestamp if available,
    // but simpler to rely on the fact that if we just navigated from Create Agent button, it's "fresh")
    // Since we now auto-create and redirect, we can check if we just arrived here.
    // However, the original request was to show this for "first time".
    // We can use a query param ?new=true
    const searchParams = new URLSearchParams(window.location.search);
    const isBrandNew = searchParams.get('new') === 'true';

    if (isBrandNew) {
      toast("Our agent is truly multilingual", {
        description: "Describe any language you want that agent to speak, and pick its accent in Voice Settings.",
        duration: 6000,
        position: "top-center",
        // new-agent-tip: index.css keeps it under dialogs.
        className: "new-agent-tip w-[400px] text-base p-4",
        action: {
          label: <X className="h-5 w-5" />,
          onClick: () => console.log("Notification dismissed"),
        },
      });
      // Clean up the URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Native tool values come from the agent's rows, which the tool function
  // mirrors into the config; the config fills in only where there is no row.
  useEffect(() => {
    const nativeTools = agentData?.config?.nativeTools;

    if (endCallTool) {
      setEndCallDescription(endCallTool.description || END_CALL_DESCRIPTION);
    }

    if (sendSmsTool) {
      setSendSmsDescription(sendSmsTool.description || "");
      setSendSmsContentType(sendSmsTool.sms_content_type || "prompt");
      setSendSmsStaticMessage(sendSmsTool.sms_static_message || "");
    } else if (nativeTools?.send_sms) {
      if (nativeTools.send_sms.description) {
        setSendSmsDescription(nativeTools.send_sms.description);
      }
      if (nativeTools.send_sms.smsContentType) {
        setSendSmsContentType(nativeTools.send_sms.smsContentType);
      }
      if (nativeTools.send_sms.smsStaticMessage) {
        setSendSmsStaticMessage(nativeTools.send_sms.smsStaticMessage);
      }
    }

    if (transferCallTool) {
      const { description, prompt } = splitDynamicPrompt(transferCallTool.description);
      setTransferCallMode(transferCallTool.transfer_call_mode || "static");
      setTransferCallDestination(transferCallTool.transfer_call_destination || "");
      setTransferCallDescription(description);
      setTransferCallPrompt(prompt);
      setTransferCallType(transferCallTool.transfer_call_type || "cold");
    } else if (nativeTools?.transfer_call) {
      setTransferCallMode(nativeTools.transfer_call.mode || "static");
      setTransferCallDestination(nativeTools.transfer_call.staticDestination || "");
      setTransferCallDescription(nativeTools.transfer_call.description || "");
      setTransferCallPrompt(nativeTools.transfer_call.dynamicPrompt || "");
      setTransferCallType(nativeTools.transfer_call.transferType || "cold");
    }
  }, [agentData, endCallTool, sendSmsTool, transferCallTool]);

  useEffect(() => {
    if (agentData) {
      setAgentName(agentData.agent_name);
      
      // Legacy cleanup: older versions appended a Knowledgebase block for the old Gemini File
      // Search knowledge base (removed 2026-10-21) to the prompt. Strip ONLY that known
      // auto-generated suffix, so users edit just their intended prompt and Publish drops it.
      let loadedPrompt = agentData.config?.systemPrompt || "";
      const legacyKbMarker = "\n\n[Knowledgebase]\nYou have a Knowledge Base tool containing:";
      const legacyIndex = loadedPrompt.indexOf(legacyKbMarker);
      if (legacyIndex !== -1) {
        loadedPrompt = loadedPrompt.substring(0, legacyIndex);
      }
      setSystemPrompt(loadedPrompt);
      setPromptBrokenOnLoad(!!analyzePrompt(loadedPrompt).error);
      setPromptEdited(false);

      // SIP URI - always update when agentData changes to avoid stale data
      if (agentData.sip_uri) {
        setSipUri(agentData.sip_uri);
      } else {
        setSipUri("");
      }

      // Voice settings
      if (typeof agentData.config?.voice === 'object') {
        const storedVoiceId = agentData.config.voice.voiceId;
        setVoiceId(isGeminiVoiceId(storedVoiceId) ? storedVoiceId : DEFAULT_GEMINI_VOICE);
      } else {
        setVoiceId(DEFAULT_GEMINI_VOICE);
      }
      // A missing or unknown model reads as 3.8 Live, which the server stores; a
      // missing or unknown voice model as the default.
      const storedModel = agentModelOf(agentData.config?.llm?.model);
      setModel(storedModel);
      setTtsModel(ttsModelOf(agentData.config?.voice?.ttsModel));
      // A level the text model does not take shows as its least, which the bot runs.
      setThinkingLevel(
        isTextModel(storedModel) ? thinkingLevelOf(storedModel, agentData.config?.settings?.thinkingLevel) : undefined,
      );
      setAccent(accentOf(agentData.config?.language));
      setVocabulary(vocabularyText(agentData.config?.vocabulary));
      setCallerLanguages(callerLanguagesOf(agentData.config?.settings?.callerLanguages));
      setTranscriber(transcriberOf(agentData.config?.transcriber));

      setToolIds(agentData.config?.toolIds || []);
      setKbStoreIds(agentData.config?.kbStoreIds || []);
      setKbMinSimilarity(kbMinSimilarityOf(agentData.config));
      setKbTopK(kbTopKOf(agentData.config));
      setKbAutoSearch(agentData.config?.kbAutoSearch === true);
      if (agentData.config?.settings?.initialMessage) {
        setInitialMessageEnabled(agentData.config.settings.initialMessage.enabled);
      } else {
        // Default to enabled if not set
        setInitialMessageEnabled(true);
      }

      if (agentData.config?.settings?.postCallAnalysis) {
        setPostCallAnalysisEnabled(agentData.config.settings.postCallAnalysis.enabled);
        setPostCallAnalysisFields(agentData.config.settings.postCallAnalysis.fields || []);
      }

      if (agentData.config?.settings) {
        if (agentData.config.settings.endCallOnSilence !== undefined) {
          setEndCallOnSilence(agentData.config.settings.endCallOnSilence);
        }
        if (agentData.config.settings.maxCallDuration !== undefined) {
          setMaxCallDuration(agentData.config.settings.maxCallDuration);
        }
        if (agentData.config.settings.silenceTimeoutMinutes !== undefined) {
          setSilenceTimeoutMinutes(agentData.config.settings.silenceTimeoutMinutes);
        }
        if (agentData.config.settings.voicemailDetection !== undefined) {
          // Back-compat: older agents stored a plain boolean here.
          const vm = agentData.config.settings.voicemailDetection;
          if (typeof vm === "boolean") {
            setVoicemailDetection(vm);
            setVoicemailAction("hangup");
            setVoicemailMessage("");
          } else if (vm && typeof vm === "object") {
            setVoicemailDetection(vm.enabled !== false);
            setVoicemailAction(vm.action === "leave_message" ? "leave_message" : "hangup");
            setVoicemailMessage(typeof vm.message === "string" ? vm.message : "");
          }
        }
        if (agentData.config.settings.webhookUrl) {
          setWebhookUrl(agentData.config.settings.webhookUrl);
        }
        if (agentData.config.settings.enableGoogleSearch !== undefined) {
          setEnableGoogleSearch(agentData.config.settings.enableGoogleSearch);
        }
        // if (agentData.config.settings.affectiveDialog !== undefined) {
        //   setAffectiveDialogEnabled(agentData.config.settings.affectiveDialog);
        // }
        // if (agentData.config.settings.proactiveAudio !== undefined) {
        //   setProactiveAudioEnabled(agentData.config.settings.proactiveAudio);
        // }
      }

      // Load web widget settings
      if (agentData.config?.webWidget) {
        setWebWidgetSettings({
          enabled: agentData.config.webWidget.enabled ?? false,
          theme: agentData.config.webWidget.theme ?? "auto",
          primaryColor: agentData.config.webWidget.primaryColor ?? "#3B82F6",
          position: agentData.config.webWidget.position ?? "bottom-right",
          buttonSize: agentData.config.webWidget.buttonSize ?? "medium",
          showBranding: agentData.config.webWidget.showBranding ?? true,
          avatarType: agentData.config.webWidget.avatarType ?? "default",
          avatarUrl: agentData.config.webWidget.avatarUrl ?? "",
          integrationMode: agentData.config.webWidget.integrationMode ?? "floating",
        });
      }

      // Whether a native tool is on, for one without a row: a missing entry is
      // off, so a deleted tool's switch does not survive to Publish. With a row,
      // the effect below derives it from toolIds instead.
      setTransferCallEnabled(agentData.config?.nativeTools?.transfer_call?.enabled || false);
      setEndCallEnabled(agentData.config?.nativeTools?.end_call?.enabled || false);
      setSendSmsEnabled(agentData.config?.nativeTools?.send_sms?.enabled || false);
    }
  }, [agentData]);

  // A native tool with a row is on exactly when its id is in toolIds, so the
  // switch in the tools list is what Publish saves. `agentData` is a dependency
  // so this also runs after the effect above has set the config's value.
  useEffect(() => {
    if (endCallTool) setEndCallEnabled(toolIds.includes(endCallTool.id));
    if (sendSmsTool) setSendSmsEnabled(toolIds.includes(sendSmsTool.id));
    if (transferCallTool) setTransferCallEnabled(toolIds.includes(transferCallTool.id));
  }, [agentData, endCallTool, sendSmsTool, transferCallTool, toolIds]);

  const handlePublish = async () => {
    // Prevent multiple simultaneous publish calls
    if (isPublishing || createAgent.isPending || updateAgent.isPending) {
      console.log('⏸️ Publish already in progress, ignoring duplicate click');
      return;
    }

    if (!currentWorkspaceId) {
      toast.error("No workspace selected");
      return;
    }

    // A prompt whose {% if %} does not parse falls back to plain substitution
    // on the call, which means the tags themselves get read aloud. Refuse the
    // save — but never trap someone whose stored prompt was already unparseable
    // before they opened the page.
    if (promptAnalysis?.error && (promptEdited || !promptBrokenOnLoad)) {
      toast.error(
        `Fix the system prompt first — line ${promptAnalysis.error.row}, column ${promptAnalysis.error.col}: ${promptAnalysis.error.message}`,
      );
      return;
    }

    if (vocabularyProblem) {
      toast.error(`Fix the words to recognise first: ${vocabularyProblem}`);
      return;
    }

    // The server refuses this config (bot.py will not start a call with it),
    // naming a config key this page does not show.
    if (transferCallEnabled && transferCallMode === "static" && !transferCallDestination.trim()) {
      toast.error("Transfer Call is on but has no destination. Edit transfer_call under Tools to add one, or switch it off.");
      return;
    }

    setIsPublishing(true);

    // Authoritative gate: the editor's analysis of the prompt may not have
    // arrived yet. Validate what we are about to write.
    const promptError = analyzePrompt(systemPrompt).error;
    if (promptError && (promptEdited || !promptBrokenOnLoad)) {
      setIsPublishing(false);
      toast.error(
        `Prompt template error at line ${promptError.row}, column ${promptError.col}: ${promptError.message}`,
      );
      return;
    }

    // Start from the stored config: the server replaces it whole, so a key this
    // page does not edit (temperature, voice speed, SIP headers, caller id…)
    // would otherwise be reset or dropped on every Publish.
    const stored = agentData?.config;
    const updatedConfig: AgentConfig = {
      ...stored,
      systemPrompt,
      llm: {
        ...stored?.llm,
        provider: "google",
        model,
        temperature: stored?.llm?.temperature ?? 0.7,
      },
      voice: {
        ...stored?.voice,
        voiceId: voiceId,
        // Read only on a text model: on 3.8 Live the stored one is kept as it is.
        ...(textModel ? { ttsModel } : {}),
      },
      // A stored value the dropdown shows as the default ("es", none) is kept until another accent is picked.
      language: accent === accentOf(stored?.language) ? (stored?.language ?? DEFAULT_ACCENT) : accent,
      // Who hears the caller on a text model: written only when the dropdown changed it, so an agent that never chose
      // one stores none (the server and the bot read none as Gemini) and a hidden dropdown keeps what is stored.
      ...(transcriber === transcriberOf(stored?.transcriber)
        ? {}
        : { transcriber: { ...stored?.transcriber, provider: transcriber } }),
      toolIds: toolIds,
      kbStoreIds: kbStoreIds,
      kbMinSimilarity,
      kbTopK,
      // Its switch shows on a text model only; hidden, the stored value is kept as it is.
      ...(textModel ? { kbAutoSearch } : {}),
      // The same for the words to recognise.
      ...(textModel ? { vocabulary: vocabularyTerms(vocabulary) } : {}),
      nativeTools: {
        transfer_call: {
          ...stored?.nativeTools?.transfer_call,
          enabled: transferCallEnabled,
          mode: transferCallMode,
          staticDestination: transferCallMode === "static" ? transferCallDestination : undefined,
          description: transferCallDescription,
          dynamicPrompt: transferCallMode === "dynamic" ? transferCallPrompt : undefined,
          callerIdMode: stored?.nativeTools?.transfer_call?.callerIdMode ?? "agent",
          transferType: transferCallType,
        },
        end_call: {
          enabled: endCallEnabled,
          description: endCallDescription,
        },
        send_sms: {
          enabled: sendSmsEnabled,
          description: sendSmsDescription,
          smsContentType: sendSmsContentType,
          smsStaticMessage: sendSmsContentType === "static" ? sendSmsStaticMessage : null,
        },
      },
      settings: {
        // Preserve settings this editor does not render. The object below is
        // built from scratch, so every key absent from it was being DESTROYED on
        // save — including ones the bot reads at call time: `vad` (per-agent VAD
        // tuning), `turnTaking` (which turn-taking implementation the agent
        // gets), `contextWindowCompression` and `maxSilencePrompts`. A customer
        // renaming their agent silently reset all of them.
        ...(agentData?.config?.settings ?? {}),
        initialMessage: {
          enabled: initialMessageEnabled,
        },
        postCallAnalysis: {
          enabled: postCallAnalysisEnabled,
          fields: postCallAnalysisFields,
        },
        endCallOnSilence: endCallOnSilence,
        maxCallDuration: maxCallDuration,
        silenceTimeoutMinutes: silenceTimeoutMinutes,
        voicemailDetection: {
          enabled: voicemailDetection,
          action: voicemailAction,
          message: voicemailAction === "leave_message" ? voicemailMessage.trim() : "",
        },
        webhookUrl: webhookUrl,
        enableGoogleSearch: enableGoogleSearch,
        // Read only on a text model: on 3.8 Live the stored level is kept as it is (the spread above). The caller
        // languages the other way round: written on 3.8 Live; on a text model only while Deepgram hears it and they
        // were changed, and otherwise the stored ones are kept.
        ...(isTextModel(model) ? { thinkingLevel: thinkingLevelOf(model, thinkingLevel) } : { callerLanguages }),
        ...(isTextModel(model) &&
        languagesShown &&
        callerLanguages.join() !== callerLanguagesOf(stored?.settings?.callerLanguages).join()
          ? { callerLanguages }
          : {}),
      },
      webWidget: webWidgetSettings,
    };

    console.log('💾 Saving agent config');

    try {
      if (isNewAgent) {
        // This path should now be rarely used given the change in Agents.tsx
        // but kept for fallback/safety or direct URL access
        const newAgent = await createAgent.mutateAsync({
          agent_name: agentName,
          agent_type: "Single Prompt",
          config: updatedConfig,
        });
        toast.success("Agent created successfully");
        navigate(`/${currentWorkspaceId}/agents/${newAgent.id}`);
      } else {
        // One PUT: the server provisions every enabled native tool, writes the
        // carried columns onto its row and resolves toolIds.
        await updateAgent.mutateAsync({
          agentId: agentId,
          updates: {
            agent_name: agentName,
            config: updatedConfig,
          },
        });
        toast.success("Agent updated successfully");
      }
    } catch (error) {
      console.error("Error saving agent:", error);
      toast.error(error instanceof Error ? `Failed to save agent: ${error.message}` : "Failed to save agent");
    } finally {
      setIsPublishing(false);
    }
  };

  const unassignKbStore = (storeId: string) =>
    setKbStoreIds((prev) => prev.filter((id) => id !== storeId));

  const isKbStoreSelected = (storeId: string) => kbStoreIds.includes(storeId);

  const agentsPath = currentWorkspaceId ? `/${currentWorkspaceId}/agents` : "/agents";

  // A deleted agent's page — a bookmark, Back, a second tab. The function
  // answers 404 for it and for an agent the user cannot access alike, so the
  // page says either. Nothing here may send the dead id anywhere (Test,
  // Publish, tools, Cal.com, the widget), so none of it renders.
  if (agentGone) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center gap-4 bg-background p-6 text-center">
        <p className="text-lg font-medium">This agent was deleted, or you don't have access to it.</p>
        <Button variant="outline" asChild>
          <Link to={agentsPath}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to agents
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="h-screen w-full flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <div className="flex-none border-b bg-card z-10">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(agentsPath)}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-medium">{agentName.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <Input
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                    className="text-lg font-semibold border-none focus-visible:ring-0 px-0 h-auto py-0"
                  />
                  <p className="text-sm text-muted-foreground">
                    Agent ID: {agentId === "new" ? "Not yet created" : agentId}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="default"
                size="lg"
                onClick={() => setTestDialogOpen(true)}
                className="bg-primary hover:bg-primary-hover text-primary-foreground shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 font-semibold px-6"
              >
                <Mic className="mr-2 h-5 w-5" />
                Test
              </Button>
              <Button
                variant="default"
                size="lg"
                onClick={handlePublish}
                // Until the agent has loaded, the form holds defaults (an empty
                // prompt, "en"), and publishing it would write them over the agent.
                disabled={isPublishing || createAgent.isPending || updateAgent.isPending || (!isNewAgent && !agentData)}
                className="bg-gradient-to-r from-primary to-purple-600 hover:from-primary-hover hover:to-purple-700 text-primary-foreground shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 font-semibold px-6"
              >
                <Save className="mr-2 h-5 w-5" />
                Publish
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Column - Main Content */}
        <div className="flex-1 p-6 flex flex-col min-w-0">
          <SystemPromptEditor
            value={systemPrompt}
            onChange={(next) => {
              setSystemPrompt(next);
              setPromptEdited(true);
            }}
            onAnalysisChange={setPromptAnalysis}
            agentId={agentId}
          />
        </div>

        {/* Right Column - Settings Sidebar */}
        <div className="w-[420px] border-l border-border bg-muted/30 flex flex-col">
          <div className="flex-none p-6 border-b border-border bg-background/50 backdrop-blur-sm z-10">
            <h2 className="text-lg font-bold">Settings</h2>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-muted-foreground/20 hover:scrollbar-thumb-muted-foreground/40 scrollbar-track-transparent">
            <div className="p-4 pb-10">
              <Accordion type="multiple" defaultValue={["voice-settings"]} className="space-y-3">
                {/* Voice Settings */}
                <AccordionItem value="voice-settings" className="border rounded-lg bg-card shadow-sm">
                  <AccordionTrigger className="hover:no-underline px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                        <Mic className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-base font-semibold">Voice Settings</span>
                    </div>

                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 space-y-4">
                    <div className="space-y-4">
                      {engineOffered && (
                        <div className="space-y-2">
                          <Label htmlFor="agent-model">Model</Label>
                          <Select value={model} onValueChange={pickModel}>
                            <SelectTrigger id="agent-model" aria-describedby="agent-model-help">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {AGENT_MODELS.map((id) => (
                                <SelectItem key={id} value={id}>
                                  {modelLabel(id)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p id="agent-model-help" className="text-xs text-muted-foreground">
                            {textModel ? MODEL_HELP.text : MODEL_HELP.live}
                          </p>
                        </div>
                      )}
                      {engineOffered && isTextModel(model) && (
                        <div className="space-y-2">
                          <Label htmlFor="agent-thinking">Thinking level</Label>
                          <Select
                            value={thinkingLevelOf(model, thinkingLevel)}
                            onValueChange={(value) => setThinkingLevel(thinkingLevelOf(model, value))}
                          >
                            <SelectTrigger id="agent-thinking" aria-describedby="agent-thinking-help">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {THINKING_LEVELS[model].map((level) => (
                                <SelectItem key={level} value={level}>
                                  {thinkingLabel(model, level)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p id="agent-thinking-help" className="text-xs text-muted-foreground">
                            {thinkingHelp(model)}
                          </p>
                        </div>
                      )}
                      {transcriberShown && (
                        <div className="space-y-2">
                          <Label htmlFor="agent-transcriber">Transcriber</Label>
                          <Select value={transcriber} onValueChange={(value) => setTranscriber(transcriberOf({ provider: value }))}>
                            <SelectTrigger id="agent-transcriber" aria-describedby="agent-transcriber-help">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TRANSCRIBERS.map((id) => (
                                <SelectItem key={id} value={id}>
                                  {TRANSCRIBER_LABELS[id]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p id="agent-transcriber-help" className="text-xs text-muted-foreground">
                            {TRANSCRIBER_HELP[transcriber]}
                          </p>
                        </div>
                      )}
                      {/* At the top, beside the model (owner, 2026-10-31), so a customer sees what it can do. */}
                      {languagesShown && (
                        <CallerLanguages
                          value={callerLanguages}
                          onChange={setCallerLanguages}
                          transcriber={transcriber}
                          showSupport={transcriberShown}
                        />
                      )}
                      <div className="space-y-2">
                        <Label htmlFor="agent-voice">Voice</Label>
                        <VoiceSelector id="agent-voice" value={voiceId} onValueChange={setVoiceId} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="agent-accent">Accent</Label>
                        <Select value={accent} onValueChange={(value) => setAccent(accentOf(value))}>
                          <SelectTrigger id="agent-accent" aria-describedby="agent-accent-help">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ACCENTS.map((code) => (
                              <SelectItem key={code} value={code}>
                                {ACCENT_LABELS[code]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p id="agent-accent-help" className="text-xs text-muted-foreground">
                          {ACCENT_HELP}
                        </p>
                      </div>
                      {engineOffered && textModel && (
                        <div className="space-y-2">
                          <Label htmlFor="agent-tts-model">Voice model</Label>
                          <Select value={ttsModel} onValueChange={(value) => setTtsModel(ttsModelOf(value))}>
                            <SelectTrigger id="agent-tts-model">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TTS_MODELS.map((id) => (
                                <SelectItem key={id} value={id}>
                                  {TTS_MODEL_LABELS[id]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {engineOffered && textModel && (
                        <div className="space-y-2">
                          <Label htmlFor="agent-vocabulary">Words to recognise</Label>
                          <Textarea
                            id="agent-vocabulary"
                            rows={3}
                            value={vocabulary}
                            onChange={(event) => setVocabulary(event.target.value)}
                            placeholder={"Acme\nGold Plan\nMain Street"}
                            aria-describedby={
                              deepgramVocabulary ? "agent-vocabulary-help agent-vocabulary-deepgram" : "agent-vocabulary-help"
                            }
                            aria-invalid={vocabularyProblem !== null}
                          />
                          <p id="agent-vocabulary-help" className="text-xs text-muted-foreground">
                            {VOCABULARY_HELP}
                          </p>
                          {deepgramVocabulary && (
                            <p id="agent-vocabulary-deepgram" className="text-xs text-muted-foreground">
                              {DEEPGRAM_VOCABULARY_NOTE}
                            </p>
                          )}
                          {vocabularyProblem && (
                            <p role="alert" className="text-xs text-destructive">
                              {vocabularyProblem}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
                        <Globe className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium">Multilingual & Expressive</p>
                          <p className="text-xs text-muted-foreground">
                            All voices are multilingual and can speak any language.
                            They can also express emotions naturally. Simply describe the
                            desired language or emotion in your system prompt, and pick the accent above.
                          </p>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Intro Message */}
                <AccordionItem value="intro-message" className="border rounded-lg bg-card shadow-sm">
                  <AccordionTrigger className="hover:no-underline px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                        <MessageSquare className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-base font-semibold">Intro Message</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 space-y-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="initial-message-enabled" className="text-base">Agent Speaks First</Label>
                          <p className="text-sm text-muted-foreground mt-1">
                            If enabled, the AI will start the conversation. If disabled, it will wait for the user to speak.
                          </p>
                        </div>
                        <Switch
                          id="initial-message-enabled"
                          checked={initialMessageEnabled}
                          onCheckedChange={setInitialMessageEnabled}
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Tools */}
                <AccordionItem value="tools" className="border rounded-lg bg-card shadow-sm">
                  <AccordionTrigger className="hover:no-underline px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                        <Wrench className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-base font-semibold">Tools</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-base">Custom Tools</Label>
                        {!isNewAgent && <AddToolDialog agentId={agentId!} runtime={toolRuntime} />}
                      </div>

                      {!isNewAgent ? (
                        <UnifiedToolsList
                          agentId={agentId!}
                          enabledToolIds={toolIds}
                          onToolsSelectionChanged={setToolIds}
                          runtime={toolRuntime}
                        />
                      ) : (
                        <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
                          <p>Save the agent to enable tools configuration.</p>
                          <Button
                            variant="outline"
                            className="mt-4"
                            onClick={handlePublish}
                            disabled={isPublishing || createAgent.isPending}
                          >
                            Save & Enable Tools
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Google Search Grounding */}
                    <div className="pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                            <Search className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <Label className="text-base">Google Search</Label>
                            <p className="text-xs text-muted-foreground mt-1">
                              Allow the agent to search Google in real-time during calls for up-to-date information.
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={enableGoogleSearch}
                          onCheckedChange={setEnableGoogleSearch}
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Knowledge Base - Coming Soon */}
                {/* <AccordionItem value="knowledge-base" className="border rounded-lg bg-card shadow-sm">
                  <AccordionTrigger className="hover:no-underline px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted">
                        <BookOpen className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-base font-semibold text-muted-foreground">Gemini Knowledge Base</span>
                        <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">Coming Soon</span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
                      <BookOpen className="w-8 h-8 mx-auto mb-3 opacity-50" />
                      <p className="font-medium">Gemini Knowledge Base Coming Soon</p>
                      <p className="text-sm mt-1">
                        In the meantime, use the Knowledge Base below for document storage and retrieval.
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem> */}

                {/* Knowledge Base */}
                <AccordionItem value="kb" className="border rounded-lg bg-card shadow-sm">
                  <AccordionTrigger className="hover:no-underline px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                        <Database className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-base font-semibold">Knowledge Base</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 space-y-4">
                    <div className="flex justify-end">
                      <KbFolderSelectorDialog
                        stores={kbStores}
                        selectedStoreIds={kbStoreIds}
                        onSelectionChanged={setKbStoreIds}
                      />
                    </div>

                    <div className="space-y-2">
                      {kbStores.filter(store => isKbStoreSelected(store.id)).length === 0 ? (
                        <p className="text-sm text-muted-foreground py-8 text-center border rounded-md border-dashed">
                          No knowledge bases assigned. Click "Assign Knowledge Base" to give this agent access to your knowledge bases.
                        </p>
                      ) : (
                        kbStores
                          .filter(store => isKbStoreSelected(store.id))
                          .map((store) => (
                            <div
                              key={store.id}
                              className="flex items-center gap-2 p-3 rounded-lg bg-card border-2 border-dashed border-primary/20 shadow-sm group hover:border-primary/50 transition-all duration-200"
                            >
                              <div className="flex items-center justify-center w-8 h-8 rounded bg-primary/10">
                                <Database className="w-4 h-4 text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium truncate block">{store.display_name}</span>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>{fileCount(store.file_count)}</span>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  unassignKbStore(store.id);
                                }}
                                title="Unassign Store"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground italic">
                      Note: Knowledge bases use vector embeddings for semantic search across your documents.
                    </p>
                    <KbMatchStrictness value={kbMinSimilarity} onChange={setKbMinSimilarity} />
                    <KbResultsPerSearch value={kbTopK} onChange={setKbTopK} />
                    {/* While offered, on a text model only, and once an assigned knowledge base holds a ready file. */}
                    {kbAutoSearchOffered && textModel && hasReadyKnowledgeBase(kbStores, kbStoreIds) && (
                      <KbAutoSearch checked={kbAutoSearch} onChange={setKbAutoSearch} />
                    )}
                    <div className="flex items-center justify-between gap-4 pt-4 border-t">
                      <p className="text-xs text-muted-foreground">
                        See what this agent's search hands over for a question, with the settings above.
                      </p>
                      <KbTestSearch
                        workspaceId={currentWorkspaceId || ""}
                        storeIds={kbStores.filter((store) => isKbStoreSelected(store.id)).map((store) => store.id)}
                        topK={kbTopK}
                        minSimilarity={kbMinSimilarity}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Cal.com Integration */}
                <AccordionItem value="cal-integration" className="border rounded-lg bg-card shadow-sm">
                  <AccordionTrigger className="hover:no-underline px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                        <BookOpen className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-base font-semibold">Cal.com Integration</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    {!isNewAgent && currentWorkspaceId ? (
                      <CalIntegrationCard agentId={agentId!} />
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <p className="mb-4">Save the agent first to enable Cal.com integration</p>
                        <Button
                          variant="outline"
                          onClick={handlePublish}
                          disabled={isPublishing || createAgent.isPending}
                        >
                          Save & Enable Integration
                        </Button>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* SIP Configuration */}
                <AccordionItem value="sip-config" className="border rounded-lg bg-card shadow-sm">
                  <AccordionTrigger className="hover:no-underline px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                        <PhoneCall className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-base font-semibold">SIP Transfer Configuration</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    {!isNewAgent ? (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>SIP URI</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              value={sipUri || "No SIP URI configured"}
                              readOnly
                              className="font-mono bg-muted"
                            />
                            {sipUri && (
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => {
                                  navigator.clipboard.writeText(sipUri);
                                  toast.success("SIP URI copied to clipboard");
                                }}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {sipUri
                              ? "This LiveKit SIP URI is unique to this agent. Point your phone system / carrier at this address to route inbound calls straight to this agent."
                              : "No SIP URI yet. It is generated automatically once the agent is saved and the LiveKit SIP host has been configured for the platform."}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Save the agent first to see the auto-generated SIP URI
                      </p>
                    )}
                  </AccordionContent>
                </AccordionItem>


                {/* Custom Analysis */}
                <AccordionItem value="custom-analysis" className="border rounded-lg bg-card shadow-sm">
                  <AccordionTrigger className="hover:no-underline px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                        <LineChart className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-base font-semibold">Custom Analysis</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <PostCallAnalysisEditor
                      config={{
                        enabled: postCallAnalysisEnabled,
                        fields: postCallAnalysisFields,
                      }}
                      onChange={(config) => {
                        setPostCallAnalysisEnabled(config.enabled);
                        setPostCallAnalysisFields(config.fields);
                      }}
                    />
                  </AccordionContent>
                </AccordionItem>

                {/* Call Settings */}
                <AccordionItem value="call-settings" className="border rounded-lg bg-card shadow-sm">
                  <AccordionTrigger className="hover:no-underline px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                        <Phone className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-base font-semibold">Call Settings</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 space-y-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>End Call on Silence</Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            End call after prolonged silence
                          </p>
                        </div>
                        <Switch
                          checked={endCallOnSilence}
                          onCheckedChange={setEndCallOnSilence}
                        />
                      </div>

                      <div>
                        <Label>Max Call Duration (minutes): {maxCallDuration}</Label>
                        <Slider
                          value={[maxCallDuration]}
                          onValueChange={([value]) => setMaxCallDuration(value)}
                          min={1}
                          max={MAX_CALL_DURATION_MINUTES}
                          step={1}
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label>Silence Timeout (minutes): {silenceTimeoutMinutes === 0 ? "Disabled" : silenceTimeoutMinutes}</Label>
                        <Slider
                          value={[silenceTimeoutMinutes]}
                          onValueChange={([value]) => setSilenceTimeoutMinutes(value)}
                          min={0}
                          max={30}
                          step={1}
                          className="mt-2"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          End call after this duration of silence (0 = disabled, 1-30 minutes)
                        </p>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label>Voicemail Detection</Label>
                            <p className="text-xs text-muted-foreground mt-1">
                              Detect voicemail / answering machines on outbound calls and end the call automatically so the agent doesn't waste time talking to a recording.
                            </p>
                          </div>
                          <Switch
                            checked={voicemailDetection}
                            onCheckedChange={setVoicemailDetection}
                          />
                        </div>

                        {voicemailDetection && (
                          <div className="space-y-3 pl-1 border-l-2 border-muted pl-4">
                            <div className="space-y-2">
                              <Label className="text-sm">On voicemail</Label>
                              <Select
                                value={voicemailAction}
                                onValueChange={(v) => setVoicemailAction(v as "hangup" | "leave_message")}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="hangup">Hang up immediately</SelectItem>
                                  <SelectItem value="leave_message">Leave a short message, then hang up</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {voicemailAction === "leave_message" && (
                              <div className="space-y-2">
                                <Label className="text-sm">Voicemail message</Label>
                                <Textarea
                                  rows={2}
                                  maxLength={240}
                                  placeholder="I'll call back at a better time. Have a good day."
                                  value={voicemailMessage}
                                  onChange={(e) => setVoicemailMessage(e.target.value)}
                                />
                                <p className="text-xs text-muted-foreground">
                                  Keep it short — 1–2 sentences. Leave blank to use the default message.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Advanced Settings */}
                <AccordionItem value="advanced-settings" className="border rounded-lg bg-card shadow-sm">
                  <AccordionTrigger className="hover:no-underline px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                        <SettingsIcon className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-base font-semibold">Advanced Settings</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 space-y-4">
                    <div className="space-y-4">

                      {/* Post-Call Webhook */}
                      <div className="pt-4 border-t space-y-4">
                        <Label className="text-sm font-medium">Post-Call Webhook</Label>
                        <div className="space-y-2">
                          <Label htmlFor="webhook-url" className="text-xs text-muted-foreground">
                            Webhook URL (POST)
                          </Label>
                          <Input
                            id="webhook-url"
                            placeholder="https://api.yourdomain.com/webhook"
                            value={webhookUrl}
                            onChange={(e) => setWebhookUrl(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">
                            Call data and transcripts will be sent to this URL when the call ends.
                          </p>
                        </div>
                      </div>

                      {/* Native Tools */}
                      {/* Native Tools configuration moved to main tools section
                    <div className="pt-4 border-t space-y-4">
                      <Label className="text-sm font-medium">Native Tools</Label>
                      
                      <div className="flex items-center justify-between">
                        <Label htmlFor="end-call">End Call</Label>
                        <Switch
                          id="end-call"
                          checked={endCallEnabled}
                          onCheckedChange={setEndCallEnabled}
                        />
                      </div>

                      {endCallEnabled && (
                        <div className="space-y-3 pl-4 border-l-2 border-border">
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <Label htmlFor="end-call-description" className="text-sm">
                                End Call Description
                              </Label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => setEndCallConfigOpen(!endCallConfigOpen)}
                                title={endCallConfigOpen ? "Close editor" : "Edit description"}
                              >
                                <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                              </Button>
                            </div>
                            {endCallConfigOpen ? (
                              <Textarea
                                id="end-call-description"
                                placeholder="Describe when the agent should end the call (e.g., 'IMMEDIATELY end and hang up the current call. Call this function when: the user says goodbye, bye, hang up, end call, I'm done, that's all, or when the conversation naturally concludes. Do not hesitate to use this.')"
                                value={endCallDescription}
                                onChange={(e) => setEndCallDescription(e.target.value)}
                                className="mt-2"
                                rows={3}
                              />
                            ) : (
                              <div className="mt-2 p-2 bg-muted/50 rounded text-sm text-muted-foreground min-h-[60px]">
                                {endCallDescription || "Click the pencil icon to configure end call description"}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <Label htmlFor="send-sms">Send SMS</Label>
                        <Switch
                          id="send-sms"
                          checked={sendSmsEnabled}
                          onCheckedChange={setSendSmsEnabled}
                        />
                      </div>

                      {sendSmsEnabled && (
                        <div className="space-y-3 pl-4 border-l-2 border-border">
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <Label htmlFor="send-sms-description" className="text-sm">
                                Send SMS Description
                              </Label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => setSendSmsConfigOpen(!sendSmsConfigOpen)}
                                title={sendSmsConfigOpen ? "Close editor" : "Edit description"}
                              >
                                <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                              </Button>
                            </div>
                            {sendSmsConfigOpen ? (
                              <Textarea
                                id="send-sms-description"
                                placeholder="Describe when the agent should send SMS messages (e.g., 'Function for sending SMS messages to users. Use this when the user requests to receive information via text message, needs a confirmation code, or wants to receive details via SMS.')"
                                value={sendSmsDescription}
                                onChange={(e) => setSendSmsDescription(e.target.value)}
                                className="mt-2"
                                rows={3}
                              />
                            ) : (
                              <div className="mt-2 p-2 bg-muted/50 rounded text-sm text-muted-foreground min-h-[60px]">
                                {sendSmsDescription || "Click the pencil icon to configure send SMS description"}
                              </div>
                            )}
                          </div>

                          <div>
                            <Label className="text-sm">SMS Content Type</Label>
                            <RadioGroup
                              value={sendSmsContentType}
                              onValueChange={(value: "prompt" | "static") =>
                                setSendSmsContentType(value)
                              }
                              className="mt-2"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="prompt" id="sms-prompt" />
                                <Label htmlFor="sms-prompt" className="font-normal cursor-pointer">
                                  Prompt (dynamic message from user)
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="static" id="sms-static" />
                                <Label htmlFor="sms-static" className="font-normal cursor-pointer">
                                  Static Sentence (predefined message)
                                </Label>
                              </div>
                            </RadioGroup>
                          </div>

                          {sendSmsContentType === "static" && (
                            <div>
                              <Label htmlFor="sms-static-message" className="text-sm">
                                Static Message
                              </Label>
                              <Textarea
                                id="sms-static-message"
                                placeholder="Enter the predefined SMS message to send"
                                value={sendSmsStaticMessage}
                                onChange={(e) => setSendSmsStaticMessage(e.target.value)}
                                className="mt-2"
                                rows={3}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <Label htmlFor="transfer-call">Transfer Call</Label>
                        <Switch
                          id="transfer-call"
                          checked={transferCallEnabled}
                          onCheckedChange={setTransferCallEnabled}
                        />
                      </div>

                      {transferCallEnabled && (
                        <div className="space-y-3 pl-4 border-l-2 border-border">
                          <div>
                            <Label htmlFor="transfer-description" className="text-sm">
                              Transfer Description
                            </Label>
                            <Textarea
                              id="transfer-description"
                              placeholder="Describe when the agent should offer to transfer calls (e.g., 'Transfer to a human agent when the user asks to speak to a real person or needs specialized help')"
                              value={transferCallDescription}
                              onChange={(e) => setTransferCallDescription(e.target.value)}
                              className="mt-2"
                              rows={3}
                            />
                          </div>

                          <div>
                            <Label className="text-sm">Transfer Mode</Label>
                            <RadioGroup
                              value={transferCallMode}
                              onValueChange={(value: "static" | "dynamic") =>
                                setTransferCallMode(value)
                              }
                              className="mt-2"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="static" id="static" />
                                <Label htmlFor="static" className="font-normal cursor-pointer">
                                  Static
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="dynamic" id="dynamic" />
                                <Label htmlFor="dynamic" className="font-normal cursor-pointer">
                                  Dynamic
                                </Label>
                              </div>
                            </RadioGroup>
                          </div>

                          {transferCallMode === "static" && (
                            <div>
                              <Label htmlFor="destination" className="text-sm">
                                Phone Number
                              </Label>
                              <Input
                                id="destination"
                                placeholder="+1234567890"
                                value={transferCallDestination}
                                onChange={(e) => setTransferCallDestination(e.target.value)}
                                className="mt-2"
                              />
                            </div>
                          )}

                          {transferCallMode === "dynamic" && (
                            <div>
                              <Label htmlFor="dynamic-prompt" className="text-sm">
                                Dynamic Prompt
                              </Label>
                              <Textarea
                                id="dynamic-prompt"
                                placeholder="Describe when and how to determine the transfer destination..."
                                value={transferCallPrompt}
                                onChange={(e) => setTransferCallPrompt(e.target.value)}
                                className="mt-2"
                              />
                            </div>
                          )}

                          <div>
                            <Label className="text-sm">Transfer Type</Label>
                            <Select
                              value={transferCallType}
                              onValueChange={(value: "cold" | "warm") =>
                                setTransferCallType(value)
                              }
                            >
                              <SelectTrigger className="mt-2">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cold">Cold Transfer</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}

                     </div>
                     */}
                    </div>
                  </AccordionContent>
                </AccordionItem>

              {/* Web Widget */}
              <AccordionItem value="web-widget" className="border rounded-lg bg-card shadow-sm">
                <AccordionTrigger className="hover:no-underline px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                      <Globe className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-base font-semibold">Web Widget</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  {!isNewAgent && currentWorkspaceId ? (
                    <WebWidgetConfig
                      agentId={agentId!}
                      workspaceId={currentWorkspaceId}
                      settings={webWidgetSettings}
                      onChange={(newSettings) => setWebWidgetSettings({
                        ...newSettings,
                        avatarUrl: newSettings.avatarUrl || "",
                      })}
                    />
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <p className="mb-4">Save the agent first to configure the web widget</p>
                      <Button 
                        variant="outline" 
                        onClick={handlePublish}
                        disabled={isPublishing || createAgent.isPending}
                      >
                        Save Agent
                      </Button>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            </div>
          </div>
        </div>
      </div>

      {/* Test Dialog */}
      <TestAgentDialog
        open={testDialogOpen}
        onOpenChange={setTestDialogOpen}
        agentId={agentId || ""}
        agentName={agentName}
        systemPrompt={systemPrompt}
      />
    </div>
  );
}
